import { useCallback } from 'react';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { useNotificationContext } from '@/modules/notifications/NotificationContext/hooks/useNotificationContext';
import { ROUTE_KEYS } from '@/navigation/constants';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';
import { diagnostics } from '@/utils/diagnostics';

import { useEnqueueMemoExportMutation } from '../graphql/EnqueueMemoExport.generated';

interface UseEnqueueMemoExportOptions {
  householdId: string;
  source: string;
}

export interface UseEnqueueMemoExportResult {
  onExport: (memoId: string) => Promise<void>;
  loading: boolean;
  error: Error | undefined;
}

function getPathToPrint(householdId: string, memoId: string) {
  const path = getCompletePathFromRouteKey(
    ROUTE_KEYS.__PRINT_ONLY_MEMO_EXPORT,
    {
      householdId,
      memoId,
    }
  );

  return path.slice(1, path.length);
}

export function useEnqueueMemoExport({
  householdId,
  source,
}: UseEnqueueMemoExportOptions): UseEnqueueMemoExportResult {
  const { showFeedback } = useFeedback();
  const { addAwaitingTargetIdForDownload } = useNotificationContext();
  const trackUserEvent = useTrackUserEvent();
  const progressFeedbackMessage =
    'Export in progress, this may take up to 1 minute';

  const [enqueueMemoExport, { loading, error }] = useEnqueueMemoExportMutation({
    onError: () => {
      showFeedback(`We weren't able to generate the PDF. Please try again.`);
    },
  });

  useReportError('Caught error when enqueuing memo export', error);

  const onExport = useCallback(
    async (memoId: string) => {
      await enqueueMemoExport({
        variables: {
          householdId,
          memoId,
          pathToPrint: getPathToPrint(householdId, memoId),
        },
        onCompleted: () => {
          diagnostics.debug('[MemoExport] Adding memo to awaiting downloads', {
            householdId,
            memoId,
          });
          addAwaitingTargetIdForDownload(memoId);
          showFeedback(progressFeedbackMessage, {
            variant: 'success',
          });
          trackUserEvent('document memo exported', {
            householdId,
            memoId,
            source,
          });
        },
      });
    },
    [
      addAwaitingTargetIdForDownload,
      enqueueMemoExport,
      householdId,
      progressFeedbackMessage,
      showFeedback,
      source,
      trackUserEvent,
    ]
  );

  return {
    onExport,
    loading,
    error,
  };
}
